import type { ReviewSourceId } from '@flexbyt/shared';
import { LogoMark, SourceGlyph } from '@flexbyt/widget-preview';

import { WidgetShowcase } from '../WidgetShowcase.js';
import { DEMO_REVIEWS } from '../../content/demo-reviews.js';
import { useFitZoom } from '../../lib/use-fit-zoom.js';
import { NightStage } from '../ui/surface.js';
import { SitePanel } from './Panel.js';

/**
 * The hero's picture: where reviews come from, and what they become.
 *
 * ---------------------------------------------------------------------------
 * The website is the subject
 * ---------------------------------------------------------------------------
 * A generated illustration of this idea was tried and dropped: it spent most
 * of its frame on a "lines into a box" diagram, drew approximations of five
 * trademarks, and shrank the one thing ReviewsJet actually sells — a review
 * section on your site — to an unreadable third of the picture.
 *
 * So this is built from the product instead. The browser takes most of the
 * width and holds the real widget renderer; every card in it carries the real
 * mark of the platform it came from. The sources sit to the left as a narrow
 * column, joined through the real ReviewsJet mark, so the diagram explains the
 * picture without competing with it. Everything is vector or live DOM, so it
 * is sharp on any screen and cannot drift from what customers get.
 *
 * The counts match the Import section further down (284 across five
 * connections) so the page never tells two different stories.
 */

const SOURCES: { id: ReviewSourceId; name: string; reviews: number }[] = [
  { id: 'google', name: 'Google', reviews: 142 },
  { id: 'trustpilot', name: 'Trustpilot', reviews: 64 },
  { id: 'tripadvisor', name: 'Tripadvisor', reviews: 38 },
  { id: 'yelp', name: 'Yelp', reviews: 26 },
  { id: 'airbnb', name: 'Airbnb', reviews: 14 },
];

/* Two from each platform, interleaved, so the grid is visibly a mix. */
const REVIEWS = [0, 1]
  .flatMap((i) =>
    SOURCES.flatMap((source) => DEMO_REVIEWS[source.id]?.slice(i, i + 1) ?? []),
  )
  // Five, so with the rating summary the grid is a clean two rows of three.
  .slice(0, 5);

export function HeroShowcase() {
  const { ref, zoom, canvas, isNarrow } = useFitZoom({
    wide: 1100,
    narrow: 560,
  });

  return (
    <NightStage innerClassName="px-4 pt-6 pb-0 sm:px-8 sm:pt-10 lg:pl-10 lg:pr-0">
      <div className="relative grid grid-cols-[minmax(0,1fr)] items-center gap-5 sm:gap-6 lg:grid-cols-[12.5rem_5.5rem_minmax(0,1fr)] lg:gap-0">
        {/* ------------------------------------------------ Sources */}
        <ul
          aria-label="Connected review sources"
          className="flex flex-wrap justify-center gap-2 lg:grid lg:h-[22rem] lg:grid-rows-5 lg:items-center lg:gap-0"
        >
          {/*
            Below `sm` each chip is the mark alone — five names and counts
            wrapped into three rows would push the website off the first
            screen of a phone.
          */}
          {SOURCES.map((source) => (
            <li
              key={source.id}
              className="flex items-center gap-2.5 rounded-2xl bg-white/[0.97] p-1.5 shadow-[0_16px_32px_-16px_rgba(0,0,0,0.7)] ring-1 ring-white/10 sm:py-2 sm:pr-3.5 sm:pl-2 lg:py-2.5"
            >
              <span className="grid size-8 shrink-0 place-items-center rounded-xl bg-surface">
                <SourceGlyph source={source.id} className="size-4.5" />
              </span>
              <span className="sr-only">{source.name}</span>
              <span aria-hidden className="hidden min-w-0 sm:block">
                <span className="block text-body-sm leading-tight font-bold text-ink-950">
                  {source.name}
                </span>
                <span className="nums block text-caption leading-tight text-ink-500">
                  {source.reviews} reviews
                </span>
              </span>
            </li>
          ))}
        </ul>

        {/* ------------------------------------------------ Connector */}
        <Connector />

        {/* ------------------------------------------------ Website */}
        <div className="relative">
          <div
            ref={ref}
            className="relative h-[26rem] overflow-hidden rounded-t-2xl bg-white shadow-[0_40px_80px_-24px_rgba(0,0,0,0.85)] ring-1 ring-white/10 sm:h-[32rem] lg:h-[34rem] lg:rounded-tr-none"
          >
            {/*
              `zoom`, not `transform: scale`: the widget measures its container
              to pick a column count, and a transform shrinks what it measures.
            */}
            <div style={{ width: canvas, zoom }}>
              <SitePanel
                domain="northgatecycles.co.uk/reviews"
                name="Northgate Cycles"
                nav={['Bikes', 'Servicing', 'Visit us']}
                cta="Book"
                className="rounded-none shadow-none ring-0"
              >
                <div
                  className={isNarrow ? 'px-6 pt-8 pb-10' : 'px-12 pt-12 pb-14'}
                >
                  <p className="eyebrow text-ink-400">Reviews</p>
                  <p className="mt-3 text-display text-ink-950">
                    What our riders say
                  </p>
                  <div className="mt-9">
                    <WidgetShowcase type="grid" reviews={REVIEWS} />
                  </div>
                </div>
              </SitePanel>
            </div>
            {/* The page continues below the stage; fade rather than slice. */}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white to-transparent" />
          </div>

          {/* The one number that ties the two halves together. */}
          <div
            aria-hidden
            className="absolute bottom-10 -left-6 hidden items-center gap-3 rounded-2xl bg-ink-950 px-4 py-3 text-white shadow-[0_24px_48px_-16px_rgba(0,0,0,0.7)] ring-1 ring-white/15 xl:flex"
          >
            <span className="relative flex size-2.5">
              <span className="absolute inline-flex size-full animate-ping rounded-full bg-emerald-400 opacity-60 motion-reduce:hidden" />
              <span className="relative inline-flex size-2.5 rounded-full bg-emerald-400" />
            </span>
            <span>
              <span className="nums block text-body-sm font-bold">
                284 reviews, one widget
              </span>
              <span className="block text-caption text-white/55">
                Synced from 5 sources
              </span>
            </span>
          </div>
        </div>
      </div>
    </NightStage>
  );
}

/**
 * Five curves from the source column converging on the ReviewsJet mark, and
 * one line on into the website. Drawn in a 0–100 box stretched to the column,
 * so the curves meet the chips however tall the column renders; the strokes
 * keep a fixed width regardless.
 */
function Connector() {
  const ys = [10, 30, 50, 70, 90];
  return (
    <div aria-hidden className="relative hidden h-[22rem] lg:block">
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        className="absolute inset-0 size-full overflow-visible"
      >
        <defs>
          <linearGradient id="rj-flow" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0" stopColor="#8fb0ff" stopOpacity="0.15" />
            <stop offset="1" stopColor="#1f5bf0" stopOpacity="0.9" />
          </linearGradient>
        </defs>
        {ys.map((y) => (
          <g key={y}>
            <path
              d={`M0 ${y} C 45 ${y}, 30 50, 62 50`}
              fill="none"
              stroke="url(#rj-flow)"
              strokeWidth="1.5"
              vectorEffect="non-scaling-stroke"
            />
            <path
              d={`M0 ${y} C 45 ${y}, 30 50, 62 50`}
              fill="none"
              stroke="#c7d6ff"
              strokeWidth="2"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
              className="rj-flow-dash"
              style={{ animationDelay: `${y * -30}ms` }}
            />
          </g>
        ))}
        <path
          d="M62 50 L100 50"
          fill="none"
          stroke="#1f5bf0"
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
        />
      </svg>

      {/* The real ReviewsJet mark, where the sources meet. */}
      <span className="absolute top-1/2 left-[62%] grid size-12 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-2xl bg-white shadow-[0_0_0_6px_rgba(31,91,240,0.18),0_16px_32px_-12px_rgba(31,91,240,0.8)]">
        <LogoMark className="h-5 text-ink-950" />
      </span>
    </div>
  );
}
